import * as THREE from 'three';
import { latLonToVec3 } from '../geo/latlon';
import { splitAtAntimeridian } from './antimeridian';
import { GLOBE_RADIUS } from '../core/globe';

/**
 * Build a single LineSegments mesh containing every country border from
 * a list of rings. Each ring is split at the antimeridian first so no
 * line streaks across the Pacific.
 *
 * One mesh + one BufferGeometry = one draw call, regardless of how many
 * countries. Modern GPUs handle hundreds of thousands of segments without
 * breaking 60fps.
 *
 * Rendered slightly above the globe surface (radius * 1.001). Minimal lift
 * to avoid z-fighting with the sphere while keeping lines visually glued
 * to the surface at all zoom angles. Design doc § 6.3 outlines a shader
 * fix if any residual parallax becomes an issue.
 */
const BORDER_RADIUS = GLOBE_RADIUS * 1.001;

// Default border color uses the design-system "ink" token (warm cream) so
// borders read as light over the dark Earth texture without feeling
// clinically white. Tried dark (#1a1f28) once — disappeared into oceans
// and dark landmasses, reverted.
const DEFAULT_BORDER_COLOR = 0xe8e4d8;

/**
 * Pure math: walk every ring, split at the antimeridian, and project each
 * segment endpoint to a 3D position. Returns the flat (x,y,z,x,y,z,...)
 * array as a Float32Array suitable for direct upload as a `position`
 * BufferAttribute.
 *
 * Separated from {@link buildBorderLinesFromPositions} so the slow
 * per-vertex projection cost can be cached at module scope and reused
 * across engine instances. WebGL resources can't cross engines, but raw
 * typed arrays can — this function produces those.
 */
export function decodeBorderPositions(rings: [number, number][][]): Float32Array {
  const positions: number[] = [];
  for (const ring of rings) {
    const subrings = splitAtAntimeridian(ring);
    for (const sub of subrings) {
      for (let i = 0; i < sub.length - 1; i++) {
        const [lonA, latA] = sub[i];
        const [lonB, latB] = sub[i + 1];
        const a = latLonToVec3(latA, lonA, BORDER_RADIUS);
        const b = latLonToVec3(latB, lonB, BORDER_RADIUS);
        positions.push(a.x, a.y, a.z, b.x, b.y, b.z);
      }
    }
  }
  return new Float32Array(positions);
}

/**
 * Wrap a pre-computed positions Float32Array in a fresh BufferGeometry +
 * LineSegments mesh. Cheap — only allocates a BufferGeometry, a Material,
 * and ships the typed array to the GPU. No per-vertex math.
 *
 * Use {@link decodeBorderPositions} to produce the input array; the result
 * can be cached and passed to this function on each new engine instance.
 */
// Opacity guidance (keep in sync when admin1 borders land):
//   - COUNTRY borders: 1.0 — always visible on their own; coastal borders
//     are half of shared ones in alpha terms, but at full opacity that
//     visual difference collapses and borders read uniform.
//   - ADMIN1 borders (future): ~0.3 — they always sit INSIDE a country
//     border that's already fully opaque, so lower opacity reads as the
//     secondary hierarchy without fighting the primary outline.
export function buildBorderLinesFromPositions(
  positions: Float32Array,
  options: { color?: THREE.ColorRepresentation; opacity?: number } = {},
): THREE.LineSegments {
  const { color = DEFAULT_BORDER_COLOR, opacity = 1.0 } = options;

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));

  const material = new THREE.LineBasicMaterial({
    color,
    opacity,
    transparent: true,
    // Don't write to depth buffer — lets the sphere render "through" the
    // lines cleanly when they're facing away from camera. Prevents visible
    // line-flicker bleed from the back of the globe at low opacity.
    depthWrite: false,
  });

  const mesh = new THREE.LineSegments(geometry, material);
  mesh.name = 'borders.countries';
  // Layer composition order (design § 7b.2): texture 0, fills 1, borders 2,
  // markers 3. Explicit so transparent-pass sort is deterministic.
  mesh.renderOrder = 2;
  return mesh;
}

/**
 * One-shot convenience: decode rings + wrap in a mesh. Use this when the
 * caller doesn't want to manage the intermediate Float32Array — e.g.
 * admin1 sub-borders, which are per-country and computed on demand without
 * cross-engine caching.
 */
export function buildBorderLines(
  rings: [number, number][][],
  options: { color?: THREE.ColorRepresentation; opacity?: number } = {},
): THREE.LineSegments {
  return buildBorderLinesFromPositions(decodeBorderPositions(rings), options);
}

/** Free GPU resources owned by a borders mesh. */
export function disposeBorderLines(mesh: THREE.LineSegments): void {
  mesh.geometry.dispose();
  (mesh.material as THREE.Material).dispose();
}
